import { AfterViewInit, Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GenericModalService, ModalMultiButton, ModalSettingsConfirmMultiButton } from '../../shared/generic-modal/generic-modal.service';

import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { CurrentUserService } from '../../user/services';

import { DeveloperConfigurationKeyComponent } from '../developer-configuration-key/developer-configuration-key.component';
import { ConfigurationKey } from '../models/configuration-key';
import { ConfigurationKeyUsage } from '../models/configuration-key-usage';
import { DataField } from '../models/data-field';
import { DataFieldGroup } from '../models/data-field-group';
import { DataFieldType } from '../models/data-field-type';
import { DeveloperService } from '../services/developer.service';
import { GridService } from '../../shared/grid/grid.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule } from 'devextreme-angular';
import { DxoEditingModule, DxoColumnFixingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule, DxoLookupModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-developer-configuration-keys',
    templateUrl: './developer-configuration-keys.component.html',
    styleUrls: ['./developer-configuration-keys.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoColumnFixingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule, DxoLookupModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule]
})
export class DeveloperConfigurationKeysComponent implements OnInit, AfterViewInit {
    @ViewChild(BizDataGridDirective, { static: true }) dataGrid: BizDataGridDirective;

    isLoading = false;
    configKeys: ConfigurationKey[] = [];
    usages: ConfigurationKeyUsage[] = [];
    dataFieldGroups: DataFieldGroup[] = [];
    dataFieldTypes: DataFieldType[] = [];
    allowAdding = false;
    allowDeleting = false;
    allowEditing = false;
    allowShowLog = false;

    constructor(
        private developerService: DeveloperService,
        private loadingPanelService: LoadingPanelService,
        private modalService: NgbModal,
        private genericModalService: GenericModalService,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        private gridService: GridService,
        public bizGridFiltervalues: BizGridFiltervalues) {
        this.onAddConfigurationKey = this.onAddConfigurationKey.bind(this);
    }

    ngOnInit(): void {
        this.allowAdding = this.currentUserService.hasPermission('Developer_Action_ConfigurationKey_Create');
        this.allowDeleting = this.currentUserService.hasPermission('Developer_Action_ConfigurationKey_Delete');
        this.allowEditing = this.currentUserService.hasPermission('Developer_Action_ConfigurationKey_Edit');
        this.allowShowLog = this.currentUserService.hasPermission('Developer_Action_ShowLog');
    }

    async ngAfterViewInit(): Promise<void> {
        await this.refresh();
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');
            this.configKeys = [];

            let keys: ConfigurationKey[] = [];
            await Promise.all([
                this.developerService.getConfigurationKeyUsages().then(result => { this.usages = result; }),
                this.developerService.getDataFieldGroups().then(result => { this.dataFieldGroups = result; }),
                this.developerService.getDataFieldTypes().then(result => { this.dataFieldTypes = result; }),
                this.developerService.getConfigurationKeys().then(result => { keys = result; })
            ]);

            this.usages.splice(0, 0, {
                KeyUsageId: -1,
                KeyUsageName: '(None)'
            });

            this.dataFieldGroups.splice(0, 0, {
                DataFieldGroupId: -1,
                Description: '',
                DisplayName: '(None)',
                SortOrder: 0
            });

            this.dataFieldTypes.splice(0, 0, {
                DateTypeId: -1,
                DataTypeName: '(None)',
                DataTypeEnum: 'Unknown'
            });

            // Ensure that these are set last
            this.configKeys = keys;
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to load data...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    onToolbarPreparing(e: any): void {
        e.toolbarOptions.items.forEach((item: any) => {
            if (item.name === 'addRowButton') {
                item.options.onClick = this.onAddConfigurationKey;
            }
        });
    }

    async refreshDataFieldGroups() {
        this.dataFieldGroups = await this.developerService.getDataFieldGroups();

        this.dataFieldGroups.splice(0, 0, {
            DataFieldGroupId: -1,
            Description: '',
            DisplayName: '(None)',
            SortOrder: 0
        });
    }

    async onAddConfigurationKey(_: any): Promise<void> {
        try {
            const modalRef = this.modalService.open(DeveloperConfigurationKeyComponent, { backdrop: 'static', size: 'lg', centered: true });
            modalRef.componentInstance.configKey = this.initNewConfigurationKey();
            modalRef.componentInstance.usages = this.usages;
            modalRef.componentInstance.dataFieldGroups = this.dataFieldGroups;
            modalRef.componentInstance.dataFieldTypes = this.dataFieldTypes;

            const result = await modalRef.result;
            if (result) {
                this.configKeys.push(await this.saveConfigurationKey(result));
            }
        }
        catch (err) {
            // We get here if the dialog is dismissed (canceled)
        }
        finally {
            await this.refreshDataFieldGroups();
        }
    }

    async editConfigKey(data: ConfigurationKey) {
        try {
            const modalRef = this.modalService.open(DeveloperConfigurationKeyComponent, { backdrop: 'static', size: 'lg', centered: true });
            modalRef.componentInstance.configKey = data;
            modalRef.componentInstance.usages = this.usages;
            modalRef.componentInstance.dataFieldGroups = this.dataFieldGroups;
            modalRef.componentInstance.dataFieldTypes = this.dataFieldTypes;

            const result = await modalRef.result;
            if (result) {
                const newConfigKey = await this.saveConfigurationKey(result);
                const index = this.configKeys.findIndex(e => e.ConfigurationKeysId === newConfigKey.ConfigurationKeysId);
                if (index >= 0) {
                    this.configKeys[index] = newConfigKey;
                }
            }
        }
        catch (err) {
            // We get here if the dialog is dismissed (canceled)
        }
        finally {
            await this.refreshDataFieldGroups();
        }
    }

    async editConfigurationKey(cellInfo: any): Promise<void> {
        await this.editConfigKey(cellInfo.data);
    }

    initNewConfigurationKey(): ConfigurationKey {
        return {
            ConfigurationKeysId: 0,
            KeyName: '',
            ConfigurationKeyUsagesId: -1,
            DataField: {
                DataFieldsId: 0,
                DataTypesId: -1,
                DisplayName: '',
                IsRequired: false,
                DataLength: 10,
                DefaultValue: '',
                Regex: '',
                RegexErrorMessage: '',
                DataFieldGroupsId: -1,
                SortOrder: 1,
                InfoText: '',
                Choices: []
            } as DataField
        } as ConfigurationKey;
    }

    onRowRemoving = (e: any): void => {
        e.cancel = this.onRowRemovingIsCancelledAsync(e);
    };

    async onRowRemovingIsCancelledAsync(e: any): Promise<boolean> {
        return !await this.onRowRemovingAsync(e);
    }

    async onRowRemovingAsync(e: any): Promise<boolean> {
        const configurationkey = e.data as ConfigurationKey;
        const partnerConfigurations = await this.developerService.getPartnerConfigurationsByKey(configurationkey.ConfigurationKeysId);
        if (partnerConfigurations?.length > 0) {
            if (!await this.getModalConfirmation('Delete', 'This will also delete ' + partnerConfigurations.length + ' values configured for partner(s) with this key. Do you want to continue?')) {
                return false;
            }
        }
        const distributionConfigurations = await this.developerService.getDistributionConfigurationsByKey(configurationkey.ConfigurationKeysId);
        if (distributionConfigurations?.length > 0) {
            if (!await this.getModalConfirmation('Delete', 'This will also delete '
            + distributionConfigurations.length + ' values configured for distribution(s) with this key. Do you want to continue?')) {
                return false;
            }
        }
        return !await this.gridService.handleDataGridRow(e.data as ConfigurationKey, item => this.removeConfigurationKey(item));
    }

    async getModalConfirmation(title: string, body: string): Promise<boolean> {
        try {
            await this.genericModalService.confirmDirect(title, body);
            return true;
        }
        catch {
            return false;
        }
    }

    async saveConfigurationKey(configKey: ConfigurationKey): Promise<ConfigurationKey> {
        const newConfigKey = await this.developerService.saveConfigurationKey(configKey);
        this.toastService.showToast('Configuration key saved...', ToastType.success);
        return newConfigKey;
    }

    async removeConfigurationKey(configKey: ConfigurationKey): Promise<ConfigurationKey> {
        await this.developerService.deleteConfigurationKey(configKey.ConfigurationKeysId);
        this.toastService.showToast('Configuration key deleted...', ToastType.success);
        return configKey;
    }
}
